import { useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * Show "Edit" instead of any UUID-shaped or base64-obfuscated id
 * segment. The panel's edit URLs are now obfuscated (see
 * `apps/panel/src/utils/idCodec`) but raw UUIDs from legacy bookmarks
 * could still leak into the breadcrumb. We mask anything that's
 * obviously an id rather than a human-readable slug.
 */
function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function looksLikeOpaqueId(s: string): boolean {
  // Bare UUID
  if (isUuid(s)) return true;
  // Numeric id (e.g. /shop/12)
  if (/^\d+$/.test(s)) return true;
  // base64url-looking string longer than 24 chars with no hyphen/space
  // (the panel's encodeId output sits comfortably here)
  if (s.length > 24 && /^[A-Za-z0-9_-]+$/.test(s) && !/^[a-z][a-z-]*$/i.test(s)) return true;
  return false;
}

export default function Breadcrumb() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  return (
    <nav className="hidden sm:flex items-center gap-1 text-sm">
      {segments.map((seg, i) => {
        const label = looksLikeOpaqueId(seg)
          ? 'Edit'
          : seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, ' ');
        const isLast = i === segments.length - 1;
        return (
          <span key={`${seg}-${i}`} className="flex items-center gap-1">
            {i > 0 && <ChevronRight size={14} className="text-content-muted" />}
            <span className={isLast ? 'text-content-primary font-medium' : 'text-content-muted'}>
              {label}
            </span>
          </span>
        );
      })}
    </nav>
  );
}
